import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import { ENV, Environment } from '@backoffice-frontend/environment'
import {
  FormMultiSelectComboField,
  FormTextfield,
  useForm,
} from '@backoffice-frontend/forms'
import {
  useMoiaErrorNotification,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { usePaceValidators } from '@backoffice-frontend/validators'
import { Button, Dialog } from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRioShareReportMutation } from '../graphql/RioShareReport.hook'

type ShareReportConfirmationDialogProps = {
  shortId: string
  onCancel: () => void
  id: string
  reportId?: string
}

const emailValues = {
  [Environment.test]: 'infieldassistance@moia.io',
  [Environment.local]: 'infieldassistance@moia.io',
  [Environment.development]: 'infieldassistance@moia.io',
  [Environment.integration]: 'infieldassistance@moia.io',
  [Environment.production]: 'support@moiab2b.zendesk.com',
  [Environment['production.us-east-1']]: 'support@moiab2b.zendesk.com',
}

const options = [{ id: '1', name: 'B2B Support', value: emailValues[ENV] }]

type FormValues = {
  sendTo: string[]
  emailAddress: string
}

const getValueByName = (name: string) =>
  options.find(option => option.name === name)?.value || ''

export const ShareReportConfirmationDialog = ({
  shortId,
  onCancel,
  id,
  reportId,
}: ShareReportConfirmationDialogProps) => {
  const { t } = useTranslation()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const [shareReport, { loading }] = useErrorHandlingMutation(
    useRioShareReportMutation,
  )
  const { control, handleSubmit, watch, reset } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      sendTo: [],
      emailAddress: '',
    },
  })
  const sendTo = watch('sendTo') || []
  const emailAddress = watch('emailAddress') || ''

  const [query, setQuery] = useState<string>('')
  const { emailPattern } = usePaceValidators()

  const parseEmails = (input: string): string[] =>
    input
      .trim()
      .split(/[\s,]+/)
      .filter(email => email.length > 0)

  const emailList = parseEmails(emailAddress)

  const isEmailValid = (emails: string[]): boolean => {
    return emails.every(email => emailPattern.value.test(email))
  }

  const isSubmitEnabled =
    sendTo.length > 0 || (emailList.length > 0 && isEmailValid(emailList))

  const onSubmit = async () => {
    const selectedEmails = sendTo.map(name => getValueByName(name))
    const recipients = Array.from(new Set([...selectedEmails, ...emailList]))

    if (reportId) {
      await shareReport({
        variables: {
          recipients,
          reportId: reportId,
        },
      }).then(data => {
        if (data?.data?.rioShareReport.success) {
          enqueueMoiaSuccessNotification(t('reportIOLib_shareReport_success'))
        } else {
          enqueueMoiaErrorNotification(t('reportIOLib_shareReport_error'))
        }
      })
    }
    handleClose()
  }

  const handleClose = () => {
    reset()
    onCancel()
  }

  return (
    <Dialog
      id={id}
      onClose={handleClose}
      actions={
        <>
          <Button
            label={t('common_cancel_button')}
            variant="secondary"
            onClick={handleClose}
          />
          <Button
            type="button"
            label={t('reportIOLib_shareReportDialog_sendEmail_button')}
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isSubmitEnabled || loading}
          />
        </>
      }
      title={t('reportIOLib_dialog_shareReport_title')}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space3,
        })}
      >
        {t('reportIOLib_dialog_shareReport_description', {
          shortId: shortId,
        })}
        <FormMultiSelectComboField
          label={t('reportIOLib_shareReportDialog_sendTo_label')}
          placeholder={t('common_select_placeholder')}
          options={options
            .map(option => option.name)
            .filter(option => option.includes(query))}
          optionToId={option => option}
          name="sendTo"
          control={control}
          query={query}
          onQueryChange={setQuery}
          size="compact"
        />
        <FormTextfield
          control={control}
          label={t('reportIOLib_shareReportDialog_emailAddress_label')}
          placeholder={t(
            'reportIOLib_shareReportDialog_emailAddress_placeholder',
          )}
          name="emailAddress"
          size="compact"
          caption={t('reportIOLib_shareReportDialog_emailAddress_caption')}
          rules={{
            validate: value => {
              const emails = parseEmails(value as string)

              if (emails.length === 0) return true

              return isEmailValid(emails)
                ? true
                : t('reportIOLib_shareReportDialog_emailAddress_error')
            },
          }}
        />
      </form>
    </Dialog>
  )
}
